import { AutoComplete } from "antd";
import { debounce } from "lodash";
import { useMemo, useState } from "react";

import { useGoogleMaps } from "../GoogleMapsApiView";
import type { Address, PlaceDetails, Prediction } from "./types";

type Option = {
  label: string;
  value: string;
};

export const AddressAutocomplete: React.FC<{
  onSelect: (address: Address) => void;
}> = ({ onSelect }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [value, setValue] = useState<string>("");
  const [options, setOptions] = useState<Option[]>([]);

  const fetchPredictions = (input: string) => {
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: "de" },
        types: ["address"],
      },
      (predictions: Prediction[] | null, status: string) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          setOptions([]);
          return;
        }

        const newOptions = predictions.map((prediction) => ({
          value: prediction.place_id,
          label: prediction.description,
        }));

        setOptions(newOptions);
      }
    );
  };

  const debouncedFetchPredictions = useMemo(
    () => debounce(fetchPredictions, 300),
    []
  );

  const handleSearch = (query: string) => {
    if (!query || query.length < 3 || !window.google || !window.google.maps) {
      setOptions([]);
      return;
    }
    debouncedFetchPredictions(query);
  };

  // Handle address selection
  const handleSelect = (placeId: string) => {
    setValue(options.find((option) => option.value === placeId)?.label || "");

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    service.getDetails(
      {
        placeId,
        fields: ["address_components"],
      },
      (placeDetails: PlaceDetails | null, status: string) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !placeDetails ||
          !placeDetails.address_components
        ) {
          return;
        }

        // Extract address components
        const addressMap: Record<string, string> = {};
        placeDetails.address_components.forEach((component) => {
          component.types.forEach((type) => {
            addressMap[type] = component.long_name;
          });
        });

        onSelect({
          street: `${addressMap.route} ${addressMap.street_number}`,
          postCode: addressMap.postal_code || "",
          city: addressMap.locality || "",
        });
      }
    );
  };

  if (!isLoaded) {
    return <div>Loading address search input...</div>;
  }

  if (loadError) {
    return (
      <div>
        Google Maps API is unavailable, please use manual address inputs instead
      </div>
    );
  }

  return (
    <AutoComplete
      allowClear
      value={value}
      onChange={(value) => setValue(value || "")}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder="Type to search for an address"
      aria-label="Type to search for an address"
      aria-describedby="address-search-hint"
      className="w-full"
    />
  );
};
